"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Link, Plus, Copy, Trash2, Edit, Clock, CheckCircle, AlertTriangle, RefreshCw } from "lucide-react"

interface BypassToken {
  id: number
  name: string
  token: string
  description: string
  expiresAt: Date
  usageCount: number
  maxUsage: number
  status: "active" | "expired" | "revoked"
  createdAt: Date
  createdBy: string
}

interface NewTokenData {
  name: string
  description: string
  duration: string
  maxUsage: string
}

export function BypassTokensContent() {
  const [tokens, setTokens] = useState<BypassToken[]>([
    {
      id: 1,
      name: "Emergency Access Token",
      token: "bypass_abc123def456ghi789jkl012mno345pqr678stu901vwx234yz",
      description: "Emergency access for critical maintenance",
      expiresAt: new Date(Date.now() + 7200000), // 2 hours from now
      usageCount: 0,
      maxUsage: 10,
      status: "active",
      createdAt: new Date(Date.now() - 3600000), // 1 hour ago
      createdBy: "admin@example.com",
    },
    {
      id: 2,
      name: "Testing Token",
      token: "bypass_test789abc012def345ghi678jkl901mno234pqr567stu890vwx",
      description: "Token for automated testing purposes",
      expiresAt: new Date(Date.now() + 86400000), // 24 hours from now
      usageCount: 5,
      maxUsage: 50,
      status: "active",
      createdAt: new Date(Date.now() - 7200000), // 2 hours ago
      createdBy: "developer@example.com",
    },
    {
      id: 3,
      name: "Expired Demo Token",
      token: "bypass_expired123456789abcdefghijklmnopqrstuvwxyz012345678",
      description: "Demo token that has expired",
      expiresAt: new Date(Date.now() - 3600000), // 1 hour ago
      usageCount: 15,
      maxUsage: 20,
      status: "expired",
      createdAt: new Date(Date.now() - 86400000), // 24 hours ago
      createdBy: "demo@example.com",
    },
  ])

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newToken, setNewToken] = useState<NewTokenData>({
    name: "",
    description: "",
    duration: "3600", // 1 hour in seconds
    maxUsage: "10",
  })

  // Safe status color getter
  const getStatusColor = useCallback((status: string): string => {
    try {
      switch (status) {
        case "active":
          return "bg-green-500/20 text-green-400 border-green-500/50"
        case "expired":
          return "bg-red-500/20 text-red-400 border-red-500/50"
        case "revoked":
          return "bg-gray-500/20 text-gray-400 border-gray-500/50"
        default:
          return "bg-slate-500/20 text-slate-400 border-slate-500/50"
      }
    } catch (error) {
      console.error("Error getting status color:", error)
      return "bg-slate-500/20 text-slate-400 border-slate-500/50"
    }
  }, [])

  // Safe time remaining calculator
  const getTimeRemaining = useCallback((expiresAt: Date): string => {
    try {
      if (!expiresAt || !(expiresAt instanceof Date) || isNaN(expiresAt.getTime())) {
        return "Invalid Date"
      }

      const now = new Date()
      const diff = expiresAt.getTime() - now.getTime()

      if (diff <= 0) return "Expired"

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

      if (hours > 0) {
        return `${hours}h ${minutes}m`
      }
      return `${minutes}m`
    } catch (error) {
      console.error("Error calculating time remaining:", error)
      return "Unknown"
    }
  }, [])

  // Safe clipboard copy
  const copyToClipboard = useCallback(async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea")
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
      }
    } catch (error) {
      console.error("Error copying to clipboard:", error)
    }
  }, [])

  // Safe token generator
  const generateToken = useCallback((): string => {
    try {
      const chars = "abcdefghijklmnopqrstuvwxyz0123456789"
      let result = "bypass_"
      for (let i = 0; i < 48; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      return result
    } catch (error) {
      console.error("Error generating token:", error)
      return "bypass_error_generating_token"
    }
  }, [])

  // Safe token creation
  const createToken = useCallback(() => {
    try {
      if (!newToken.name.trim()) {
        console.error("Token name is required")
        return
      }

      const token: BypassToken = {
        id: Date.now(),
        name: newToken.name.trim(),
        token: generateToken(),
        description: newToken.description.trim(),
        expiresAt: new Date(Date.now() + Number.parseInt(newToken.duration) * 1000),
        usageCount: 0,
        maxUsage: Math.max(1, Number.parseInt(newToken.maxUsage) || 10),
        status: "active",
        createdAt: new Date(),
        createdBy: "admin@example.com",
      }

      setTokens((prev) => [token, ...prev])
      setNewToken({ name: "", description: "", duration: "3600", maxUsage: "10" })
      setIsCreateDialogOpen(false)
    } catch (error) {
      console.error("Error creating token:", error)
    }
  }, [newToken, generateToken])

  // Safe token revocation
  const revokeToken = useCallback((id: number) => {
    try {
      setTokens((prev) => prev.map((token) => (token.id === id ? { ...token, status: "revoked" as const } : token)))
    } catch (error) {
      console.error("Error revoking token:", error)
    }
  }, [])

  // Safe token deletion
  const deleteToken = useCallback((id: number) => {
    try {
      setTokens((prev) => prev.filter((token) => token.id !== id))
    } catch (error) {
      console.error("Error deleting token:", error)
    }
  }, [])

  // Safe token filtering
  const activeTokens = tokens.filter((token) => token.status === "active")
  const expiredTokens = tokens.filter((token) => token.status === "expired" || token.status === "revoked")
  const expiringTokens = activeTokens.filter((token) => {
    try {
      return new Date(token.expiresAt).getTime() - Date.now() < 3600000
    } catch (error) {
      return false
    }
  })

  // Safe date formatting
  const formatDate = useCallback((date: Date): string => {
    try {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return "Invalid Date"
      }
      return date.toLocaleDateString()
    } catch (error) {
      console.error("Error formatting date:", error)
      return "Unknown Date"
    }
  }, [])

  // Safe number formatting
  const formatNumber = useCallback((num: number): string => {
    try {
      if (typeof num !== "number" || isNaN(num)) return "0"
      return num.toString()
    } catch (error) {
      return "0"
    }
  }, [])

  return (
    <>
      {/* Bypass Tokens Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-slate-900/50 border-green-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="h-6 w-6 text-green-500" />
              <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Active</Badge>
            </div>
            <div className="text-2xl font-bold text-green-400">{activeTokens.length}</div>
            <div className="text-sm text-slate-400">Active Tokens</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-amber-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50">Expiring</Badge>
            </div>
            <div className="text-2xl font-bold text-amber-400">{expiringTokens.length}</div>
            <div className="text-sm text-slate-400">Expiring Soon</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-red-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="h-6 w-6 text-red-500" />
              <Badge className="bg-red-500/20 text-red-400 border-red-500/50">Expired</Badge>
            </div>
            <div className="text-2xl font-bold text-red-400">{expiredTokens.length}</div>
            <div className="text-sm text-slate-400">Expired/Revoked</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-blue-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <RefreshCw className="h-6 w-6 text-blue-500" />
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">Usage</Badge>
            </div>
            <div className="text-2xl font-bold text-blue-400">
              {tokens.reduce((sum, token) => sum + (token.usageCount || 0), 0)}
            </div>
            <div className="text-sm text-slate-400">Total Usage</div>
          </CardContent>
        </Card>
      </div>

      {/* Token Management */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center">
              <Link className="mr-2 h-5 w-5 text-purple-500" />
              Bypass Token Management
            </CardTitle>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Token
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700">
                <DialogHeader>
                  <DialogTitle className="text-slate-100">Create New Bypass Token</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-300">Token Name</Label>
                    <Input
                      placeholder="Enter token name..."
                      value={newToken.name}
                      onChange={(e) => setNewToken({ ...newToken, name: e.target.value })}
                      className="bg-slate-800/50 border-slate-700 mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-sm text-slate-300">Description</Label>
                    <Textarea
                      placeholder="Enter token description..."
                      value={newToken.description}
                      onChange={(e) => setNewToken({ ...newToken, description: e.target.value })}
                      className="bg-slate-800/50 border-slate-700 mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-300">Duration</Label>
                      <Select
                        value={newToken.duration}
                        onValueChange={(value) => setNewToken({ ...newToken, duration: value })}
                      >
                        <SelectTrigger className="bg-slate-800/50 border-slate-700 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3600">1 Hour</SelectItem>
                          <SelectItem value="21600">6 Hours</SelectItem>
                          <SelectItem value="86400">24 Hours</SelectItem>
                          <SelectItem value="604800">7 Days</SelectItem>
                          <SelectItem value="2592000">30 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm text-slate-300">Max Usage</Label>
                      <Input
                        type="number"
                        placeholder="10"
                        value={newToken.maxUsage}
                        onChange={(e) => setNewToken({ ...newToken, maxUsage: e.target.value })}
                        className="bg-slate-800/50 border-slate-700 mt-1"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={createToken}
                      disabled={!newToken.name.trim()}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Create Token
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tokens.length > 0 ? (
              tokens.map((token) => (
                <div key={token.id} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-200">{token.name || "Unnamed Token"}</h3>
                        <Badge className={`${getStatusColor(token.status)} text-xs`}>
                          {(token.status || "unknown").toUpperCase()}
                        </Badge>
                        {token.status === "active" && (
                          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50 text-xs">
                            {getTimeRemaining(token.expiresAt)}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-400 mb-2">{token.description || "No description"}</p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(token.token)}
                        className="h-8 w-8"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Edit className="h-4 w-4" />
                      </Button>
                      {token.status === "active" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => revokeToken(token.id)}
                          className="h-8 w-8 text-amber-400 hover:text-amber-300"
                        >
                          <AlertTriangle className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteToken(token.id)}
                        className="h-8 w-8 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="bg-slate-900/50 rounded p-3 border border-slate-700/30 mb-3">
                    <div className="text-xs text-slate-500 mb-1">Token:</div>
                    <div className="text-xs font-mono text-slate-300 break-all">{token.token}</div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-slate-500">Usage</div>
                      <div className="text-slate-300 font-medium">
                        {formatNumber(token.usageCount)} / {formatNumber(token.maxUsage)}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500">Created</div>
                      <div className="text-slate-300 font-medium">{formatDate(token.createdAt)}</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Expires</div>
                      <div className="text-slate-300 font-medium">{formatDate(token.expiresAt)}</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Created By</div>
                      <div className="text-slate-300 font-medium">{token.createdBy || "Unknown"}</div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Link className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <div className="text-slate-400">No bypass tokens created yet</div>
                <div className="text-sm text-slate-500 mt-1">Create your first token to get started</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  )
}
